import { Wallet } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { useRewardsLeaderboard } from "@/hooks/useRewardsLeaderboard";
import { ZERO_ADDRESS } from "../helper";
import { useEvmWallet } from "../hooks/useEvmWallet";
import { useGetShinkaiNamespaces } from "../hooks/useGetShinkaiNamespaces";
import { addNFTToWallet } from "../tokens";
import type { Identity } from "../types";
import { env } from "../utils/envs";
import { IdentityDataInfoForm } from "./IdentityDataInfoForm";
import IdentityStakedAmountForm from "./IdentityStakedAmountForm";
import UnclaimIdentityButton from "./UnclaimIdentityButton";

type Props = {
	identityData: Identity;
	refetch: () => void;
};

export default function IdentityInfoTabContent({
	identityData,
	refetch,
}: Props) {
	const { address } = useEvmWallet();
	const { data: shinkaiNamespaces } = useGetShinkaiNamespaces();
	const isOwner =
		identityData?.nft.owner.toLowerCase() === address?.toLowerCase();
	const tokenId = identityData?.nft.tokenId ?? "";

	const identity = identityData.identityRaw;
	const identityName = identity.substring(0, identity.lastIndexOf("."));
	const namespaceString = identity.substring(identity.lastIndexOf("."));
	const namespace = shinkaiNamespaces[namespaceString];

	const { data: leaderboard = [] } = useRewardsLeaderboard();
	const rewardsPoints = useMemo(() => {
		const match = leaderboard.find(
			(entry) => entry.identity === identityData.identityRaw,
		);
		return match?.points ?? 0;
	}, [leaderboard, identityData.identityRaw]);

	const truncateAddress = (addr: string) => {
		if (!addr) return "";
		return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
	};

	return (
		<div className="space-y-6">
			{/* Owner & NFT Info */}
			<div className="space-y-3">
				<div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
					<div className="flex items-center gap-2">
						<span className="text-sm font-medium text-text-tertiary">
							Owner:
						</span>
						<span className="text-sm text-text-default font-mono break-all">
							{identityData.nft.owner === ZERO_ADDRESS
								? "None"
								: truncateAddress(identityData.nft.owner)}
						</span>
					</div>
				</div>
				<div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
					<div className="flex items-center gap-2">
						<span className="text-sm font-medium text-text-tertiary">
							NFT ID:
						</span>
						<span className="text-sm text-text-default">{tokenId}</span>
					</div>
					{isOwner && (
						<Button
							variant="outline"
							size="xs"
							onClick={() => addNFTToWallet(tokenId)}
							className="gap-2 border-divider w-fit"
						>
							<Wallet className="w-3.5 h-3.5" />
							Add to wallet
						</Button>
					)}
				</div>
			</div>

			<div className="h-px bg-divider" />

			{/* Identity Data Form */}
			<IdentityDataInfoForm
				identityData={identityData}
				isOwner={isOwner}
				refetchIdentityData={refetch}
			/>

			<div className="h-px bg-divider" />

			{/* Staking Form */}
			<IdentityStakedAmountForm
				stakedAmount={BigInt(identityData.stakedTokens)}
				rewardsPoints={rewardsPoints}
				identity={identityData.identityRaw}
				name={identityName}
				namespace={namespace}
				isOwner={isOwner}
				refetchIdentityData={refetch}
			/>

			{/* Unclaim Button */}
			{isOwner && (
				<>
					<div className="h-px bg-divider" />
					<div className="flex justify-center">
						<UnclaimIdentityButton identity={identityData.identityRaw} />
					</div>
				</>
			)}
		</div>
	);
}
